import { type RefObject, useCallback, useEffect } from 'react'

/**
 * Which way a panel opens from its trigger along the block axis.
 *
 * `forward` is after it — the ordinary drop-down, and what almost everything
 * wants. `backward` is before it, which a panel opened from inside a long
 * document asks for: what lies after the trigger is what the reader has not
 * reached yet, and covering that is worse than covering what they have.
 */
export type PopoverSide = 'forward' | 'backward'

/** How close a panel may come to the window's edge before it is moved. */
const GUTTER = 12

/**
 * Keep a floating panel on screen without moving it away from its trigger.
 *
 * Both halves matter, and each rules out an answer that satisfies only the other.
 * A panel that hangs off its trigger and runs past the window edge is not
 * visible — a tooltip near the trailing edge of a table header had the first half
 * of every line cut away. A panel pinned to a corner of the viewport is visible
 * and has lost its trigger, so it reads as a bug even while behaving.
 *
 * So the panel stays exactly where CSS put it and is then nudged along the inline
 * axis by however much it overhangs — the minimum, which is what keeps it
 * attached. `--popover-shift` is the only thing written; the stylesheet decides
 * what to do with it (`translate`), and a panel that already fits is not moved.
 *
 * Shared by every floating thing in the app rather than solved per component:
 * `usePopover` for the menus, `Tooltip` for the hover labels. The alternative is
 * this arithmetic copied five times and diverging, which is the same failure the
 * one-dismissal rule exists to prevent.
 *
 * The block axis is answered by a SIDE rather than a nudge — see `PopoverSide`.
 *
 * `position-anchor` will do this natively. Until it is everywhere, one measure
 * after open is the honest version.
 */
export function useClampToViewport(
  panelRef: RefObject<HTMLElement | null>,
  active: boolean,
  prefer: PopoverSide = 'forward',
) {
  const clamp = useCallback(() => {
    const panel = panelRef.current
    if (!panel) return
    // Measure with no correction applied, so a resize re-solves from scratch
    // rather than accumulating nudges.
    panel.style.setProperty('--popover-shift', '0px')

    /*
     * The block axis is a SIDE rather than a nudge: a panel pushed along it
     * ends up over its own trigger, which is the one thing it must not hide.
     * So it opens on the side it asked for and turns to the other only where
     * that side has no room and the other genuinely does — a turn into a
     * second way of being clipped buys nothing.
     *
     * The stylesheet owns both positions, since each panel's geometry is its
     * own; this decides which of them applies.
     */
    panel.dataset.side = prefer
    const other: PopoverSide = prefer === 'forward' ? 'backward' : 'forward'
    const spills = (side: PopoverSide, r: DOMRect) =>
      side === 'forward'
        ? r.bottom > window.innerHeight - GUTTER
        : r.top < GUTTER

    if (spills(prefer, panel.getBoundingClientRect())) {
      panel.dataset.side = other
      if (spills(other, panel.getBoundingClientRect()))
        panel.dataset.side = prefer
    }

    const rect = panel.getBoundingClientRect()
    const overRight = rect.right - (window.innerWidth - GUTTER)
    const overLeft = GUTTER - rect.left
    /*
     * Both can be true only when the panel is wider than the window, and then
     * the leading edge wins: a panel you can read the start of beats one centered
     * and clipped at both ends. The stylesheet caps the width so that stays rare.
     */
    const shift = overLeft > 0 ? overLeft : overRight > 0 ? -overRight : 0
    if (shift !== 0) panel.style.setProperty('--popover-shift', `${shift}px`)
  }, [panelRef, prefer])

  useEffect(() => {
    if (!active) return
    clamp()

    /* Coalesced into a frame, because the thing that scrolls may be moving
       every frame of its own accord — a sheet that scrolls itself would
       otherwise measure the panel once per scroll event on top of that. */
    let frame = 0
    const onMove = () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        clamp()
      })
    }

    window.addEventListener('resize', onMove)
    // Capture, because the thing that scrolls is usually an inner region — a
    // table's own scroll area, a sheet's body — not the window.
    window.addEventListener('scroll', onMove, true)

    /*
     * And when the PANEL changes size, which is the case the two listeners
     * above cannot see: the window has not resized and nothing has scrolled,
     * but a panel that grew after it opened — options that arrived, a label
     * that turned out longer than the one measured — is a panel whose position
     * was solved against a width it no longer has. Its width cap keeps it
     * inside the WINDOW; nothing but this keeps it inside the window from
     * wherever its trigger happens to stand.
     */
    const resized = new ResizeObserver(onMove)
    if (panelRef.current) resized.observe(panelRef.current)

    return () => {
      if (frame) cancelAnimationFrame(frame)
      window.removeEventListener('resize', onMove)
      window.removeEventListener('scroll', onMove, true)
      resized.disconnect()
    }
  }, [active, clamp, panelRef])
}
